import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyTelegramColors,
  getTelegramUser,
  getTheme,
  getThemePreference,
  saveThemePreference,
  isTelegram,
  type ThemePreference,
} from '../services/telegram';
import { applyDesign, getDesign, saveDesign, getCategoryStyle, saveCategoryStyle, applyCategoryStyle, type DesignId, type CategoryStyle } from '../services/design';
import { applyUiMode, getUiMode, saveUiMode, type UiMode } from '../services/mode';
import WebApp from '@twa-dev/sdk';

interface TelegramUser {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
}

interface TelegramContextValue {
  user: TelegramUser | null;
  theme: 'light' | 'dark';
  /** Выбор пользователя: dark/light/system (сам по себе не «итоговая» тема) */
  themePreference: ThemePreference;
  setThemePreference: (pref: ThemePreference) => void;
  isTelegram: boolean;
  design: DesignId;
  setDesign: (id: DesignId) => void;
  categoryStyle: CategoryStyle;
  setCategoryStyle: (style: CategoryStyle) => void;
  /** Простой (дефолт) / полный режим интерфейса */
  uiMode: UiMode;
  setUiMode: (mode: UiMode) => void;
}

const TelegramContext = createContext<TelegramContextValue>({
  user: null,
  theme: 'dark',
  themePreference: 'dark',
  setThemePreference: () => {},
  isTelegram: false,
  design: 'ukraine',
  setDesign: () => {},
  categoryStyle: 'photo',
  setCategoryStyle: () => {},
  uiMode: 'simple',
  setUiMode: () => {},
});

export function TelegramProvider({ children }: { children: ReactNode }) {
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>(getThemePreference);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => getTheme(getThemePreference()));
  const [design, setDesignState] = useState<DesignId>(getDesign);
  const [categoryStyle, setCategoryStyleState] = useState<CategoryStyle>(getCategoryStyle);
  const [uiMode, setUiModeState] = useState<UiMode>(getUiMode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    applyDesign(design);
  }, [design]);

  useEffect(() => {
    applyUiMode(uiMode);
  }, [uiMode]);

  useEffect(() => {
    applyCategoryStyle(categoryStyle);
  }, [categoryStyle]);

  // Цвета шапки/фона Telegram должны соответствовать выбранному дизайну
  useEffect(() => {
    applyTelegramColors(design, theme);
  }, [design, theme]);

  // Смена темы Telegram/ОС меняет приложение только в режиме «Системна»
  useEffect(() => {
    try {
      const handler = () => {
        if (getThemePreference() === 'system') setTheme(getTheme('system'));
      };
      WebApp.onEvent('themeChanged', handler);
      return () => {
        WebApp.offEvent('themeChanged', handler);
      };
    } catch {}
  }, []);

  const value = useMemo<TelegramContextValue>(
    () => ({
      user: getTelegramUser(),
      theme,
      themePreference,
      setThemePreference: (pref: ThemePreference) => {
        saveThemePreference(pref);
        setThemePreferenceState(pref);
        setTheme(getTheme(pref));
      },
      isTelegram: isTelegram(),
      design,
      setDesign: (id: DesignId) => {
        saveDesign(id);
        setDesignState(id);
      },
      categoryStyle,
      setCategoryStyle: (style: CategoryStyle) => {
        saveCategoryStyle(style);
        setCategoryStyleState(style);
      },
      uiMode,
      setUiMode: (mode: UiMode) => {
        saveUiMode(mode);
        setUiModeState(mode);
      },
    }),
    [theme, themePreference, design, categoryStyle, uiMode],
  );

  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>;
}

export function useTelegramContext() {
  return useContext(TelegramContext);
}
